import type { ReactNode } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { useTranslations } from 'next-intl'

import { HtmlScope } from '@/components/encuentro/marketing/html-scope'
import { authLinkClass } from '@/components/encuentro/screens/auth/auth-shell'
import { ENCUENTRO_SCOPE_ATTRIBUTE, ENCUENTRO_SCOPE_VALUE } from '@/lib/design/encuentro-scope'

/**
 * The page of the new-school sign-up at `/start` (ALU-118). It continues the
 * public site (ALU-14): the Alumnia lockup on the left of a light top bar that
 * goes back home, "¿Ya tenés cuenta? Ingresá" and the language on the right,
 * and a short legal footer. No site navigation: the visitor already chose to
 * start, so the page keeps them on the form.
 *
 * It is its own scroll container (`fixed`, like `AuthShell`): the app pins
 * `html` and `body` to the viewport and `/start` has no layout that frees them.
 *
 * Like the public site's `MarketingShell`, it carries the Encuentro scope
 * itself (on its root on the server, on `<html>` once mounted), because
 * `/start` is Encuentro for everyone, whatever the UI switch says.
 */
export function SignupShell({
  locale,
  children,
}: {
  /** The language switcher, top right. */
  locale?: ReactNode
  children: ReactNode
}) {
  const t = useTranslations('encuentro.schoolSignup.shell')
  const tBrand = useTranslations('encuentro.auth.brand')
  const scope = { [ENCUENTRO_SCOPE_ATTRIBUTE]: ENCUENTRO_SCOPE_VALUE }
  return (
    <div
      {...scope}
      data-slot="signup-shell"
      className="fixed inset-0 grid grid-cols-[minmax(0,1fr)] grid-rows-[auto_1fr_auto] overflow-y-auto bg-en-bg font-en-sans text-en-ink antialiased pt-safe pb-safe pl-safe pr-safe"
    >
      <HtmlScope />
      <header className="border-b border-en-line">
        <div className="mx-auto flex min-h-16 w-full max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-3 md:px-6">
          <Link
            href="/"
            aria-label={t('home')}
            className="rounded-en-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid focus-visible:outline-en-focus"
          >
            <Image
              src="/brand/encuentro/lockup-horizontal-blue.svg"
              alt={tBrand('alumnia')}
              width={124}
              height={32}
              priority
              unoptimized
              className="h-8 w-auto"
            />
          </Link>
          <div className="flex flex-wrap items-center justify-end gap-3">
            <p className="text-en-meta text-en-ink-muted">
              <span className="max-sm:sr-only">{t('haveAccount')} </span>
              <Link href="/login" className={`font-semibold ${authLinkClass}`}>
                {t('signIn')}
              </Link>
            </p>
            {locale}
          </div>
        </div>
      </header>
      <main
        id="main"
        tabIndex={-1}
        className="grid grid-cols-[minmax(0,1fr)] content-start outline-none"
      >
        {children}
      </main>
      <footer className="border-t border-en-line">
        <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-4 text-en-caption text-en-ink-muted md:px-6">
          <span>{t('copyright', { year: new Date().getFullYear() })}</span>
          <nav className="flex gap-4">
            <Link href="/policies/terms" className={authLinkClass}>
              {t('terms')}
            </Link>
            <Link href="/policies/privacy" className={authLinkClass}>
              {t('privacy')}
            </Link>
          </nav>
        </div>
      </footer>
      {/* ALU-454: the cookie banner is fixed over the bottom of this scroll
          container; this spacer lets the last field scroll clear of it. */}
      <div aria-hidden className="h-[var(--cookie-banner-height,0px)]" />
    </div>
  )
}
